import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Cable, Loader2, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useDmpWriteConfirm } from '@/hooks/useDmpWriteConfirm';
import { useAuth } from '@/hooks/useAuth';
import { releaseAddress, deriveNetworkIds } from '@/hooks/useAddressValidation';
import { FakeOdfModal } from '@/components/dmp/FakeOdfModal';
import type { DmpAddressAttributes, ServiceFileCheckId } from '@uniticket/shared';

/** Les trois identifiants réseau : ce que « Déduire du bâtiment » comble, et la seule réparation que la fiche par
 *  logement de la fenêtre de validation offre (D2708-2). */
export const NETWORK_ID_CHECKS: readonly ServiceFileCheckId[] = ['areaId', 'areaPopId', 'centralPopId'];

// Les boutons qui RÉPARENT une condition bloquante du verdict « visible chez le partenaire ».
//
// ⚠️ CONSOLE DMP, À UNE EXCEPTION PRÈS (contrainte user 2026-08-31, assouplie par lui le 2026-10-15). Ce fichier
// vit sous components/dmp/ et sert la console en entier. La fenêtre de validation ne l'appelle que pour sa fiche
// par logement, restreint à NETWORK_ID_CHECKS (`opts.only`) et sur un MDU seulement — c'est la page qui en décide.
// Raison (D2708-2, ticket …001113) : le release y refusait une boîte sans PoP — jamais passée en build list, donc
// jamais dotée d'un PoP — sans rien offrir pour la réparer, alors que la réponse était sur le logement d'à côté.
// Tout le reste — Publier, Remettre en scope, le fake ODF — reste propre à la console.
//
// Chaque bouton appelle une route DÉJÀ gardée et journalisée plutôt qu'un UPDATE fabriqué pour
// l'occasion : le trou n'était pas l'absence de chemins d'écriture, c'était que la console n'y avait
// pas accès. Chaque action passe par la confirmation d'écriture DMP commune.
//
// Trois conditions n'ont volontairement PAS de bouton :
//  • `status` — il se déduit des actions (décision user) ; sa correction manuelle est une action
//    d'exception à part, avec motif obligatoire, pas un raccourci depuis le verdict.
//  • `deploymentList` — remettre une adresse dans la liste de déploiement est une décision de scope,
//    pas une réparation de donnée.
//  • `deliveryStatus` — le sélecteur existe déjà dans la fiche d'appartement ; le dupliquer ici
//    ouvrirait un second chemin d'écriture pour la même colonne.
//
// `fiber` en avait un quatrième, retiré le 2026-09-02, et son raisonnement était faux : « le filet
// fake-ODF tourne DÉJÀ dans le release, donc Publier pose la fibre au passage » suppose que « Publier »
// soit à l'écran. Il ne l'est que si une condition de release est ROUGE — or sur une adresse déjà
// publiée à qui il ne manque que la fibre (cas réel jvid 13500551 : neuf conditions vertes), aucun
// bouton n'apparaissait. Le fake ODF a donc désormais son propre chemin, avec aperçu avant écriture.
export function useServiceFileFixes(
  jvid: number | null,
  attributes: DmpAddressAttributes | null,
  onDone: () => void,
  addressLabel?: string | null,
  /** `only` restreint les boutons à ces conditions ; sans lui, la console reçoit tout. */
  opts: { only?: readonly ServiceFileCheckId[] } = {},
): { renderFix: (id: ServiceFileCheckId) => ReactNode; fixModal: ReactNode } {
  const { t } = useTranslation(['tickets']);
  const confirmDmpWrite = useDmpWriteConfirm();
  const { isAdmin } = useAuth();
  const [busy, setBusy] = useState<ServiceFileCheckId | null>(null);
  const [fakeOdfOpen, setFakeOdfOpen] = useState(false);

  async function run(id: ServiceFileCheckId, label: string, fn: () => Promise<string>) {
    if (jvid == null) return;
    if (!confirmDmpWrite(`${label} — JVID ${jvid}`)) return;
    setBusy(id);
    try {
      toast.success(await fn());
      onDone();
    } catch (err) {
      toast.error((err as Error)?.message || t('tickets:dmpLookup.fix.error'));
    } finally {
      setBusy(null);
    }
  }

  /** Rendu d'un bouton pour la condition donnée, ou null quand elle n'a pas de réparation offerte.
   *  `go` écrit tout de suite (la réparation est décidée d'avance) ; `open` ouvre un écran d'aperçu
   *  quand il reste quelque chose à montrer ou à choisir avant d'écrire. */
  function renderFix(id: ServiceFileCheckId): ReactNode {
    if (jvid == null || attributes == null) return null;
    if (opts.only && !opts.only.includes(id)) return null;

    const fixes: Partial<Record<ServiceFileCheckId, { label: string; go?: () => Promise<string>; open?: () => void }>> = {
      // Release écrit les DEUX drapeaux (et huit autres colonnes) d'un coup : c'est précisément ce qui
      // empêche de recréer l'état à moitié publié où home_released est coché seul.
      approvedForRelease: {
        label: t('tickets:dmpLookup.fix.publish'),
        go: async () => {
          await releaseAddress(jvid, {});
          return t('tickets:dmpLookup.fix.publishDone');
        },
      },
      // Le release remet aussi out_of_scope_at à NULL — d'où le même bouton pour ces trois conditions.
      homeReleased: {
        label: t('tickets:dmpLookup.fix.publish'),
        go: async () => {
          await releaseAddress(jvid, {});
          return t('tickets:dmpLookup.fix.publishDone');
        },
      },
      inScope: {
        label: t('tickets:dmpLookup.fix.backInScope'),
        go: async () => {
          await releaseAddress(jvid, {});
          return t('tickets:dmpLookup.fix.backInScopeDone');
        },
      },
      areaId: { label: t('tickets:dmpLookup.fix.derive'), go: derive },
      areaPopId: { label: t('tickets:dmpLookup.fix.derive'), go: derive },
      centralPopId: { label: t('tickets:dmpLookup.fix.derive'), go: derive },
      // Le seul qui passe par un aperçu : le PoP se DÉDUIT, et une déduction qu'on n'a pas vue n'est
      // pas une déduction qu'on a acceptée. L'écran dit d'où il vient avant d'écrire.
      //
      // Et le seul réservé aux admins : sa route est gardée SYSTEM_ADMIN comme les autres écritures
      // /dmp/console/*, alors que Publier et Déduire sont ouvertes aux opérateurs Unifiber. Ce panneau
      // s'ouvre aussi en tiroir depuis n'importe quel JVID de l'app — offrir un bouton qui répondra 403
      // serait promettre une réparation qu'on refuse ensuite.
      ...(isAdmin ? { fiber: { label: t('tickets:dmpLookup.fix.fakeOdf'), open: () => setFakeOdfOpen(true) } } : {}),
    };

    async function derive(): Promise<string> {
      const r = await deriveNetworkIds(jvid!);
      const d = r.data;
      // Dire D'OÙ vient la valeur, pas seulement qu'elle a été posée : c'est ce qui permet de la
      // contester. Le jvid source est nommé pour que l'admin puisse aller le regarder.
      // La source se dit dans la langue de l'écran : le jeton brut du serveur (« sibling ») s'affichait tel quel.
      const parts: string[] = [];
      if (d.areaId) {
        parts.push(t('tickets:dmpLookup.fix.deriveArea', {
          value: d.areaId.value, from: t(`tickets:dmpLookup.fix.from.${d.areaId.from}`),
        }));
      }
      if (d.pop) {
        parts.push(t('tickets:dmpLookup.fix.derivePop', {
          areaPop: d.pop.areaPopId, central: d.pop.centralPopId,
          from: t(`tickets:dmpLookup.fix.from.${d.pop.from}`), jvid: d.pop.sourceJvid,
        }));
      }
      if (d.unresolved.length > 0) {
        parts.push(t('tickets:dmpLookup.fix.deriveUnresolved', { fields: d.unresolved.join(', ') }));
      }
      return parts.length ? parts.join(' · ') : t('tickets:dmpLookup.fix.deriveNothing');
    }

    const fix = fixes[id];
    if (!fix) return null;
    return (
      <Button
        size="sm"
        variant="outline"
        disabled={busy != null}
        onClick={() => { if (fix.open) fix.open(); else if (fix.go) void run(id, fix.label, fix.go); }}
        className="ml-2 h-6 align-middle px-2 text-[11px]"
      >
        {busy === id
          ? <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          : fix.open
            ? <Cable className="mr-1 h-3 w-3" />
            : <Wand2 className="mr-1 h-3 w-3" />}
        {fix.label}
      </Button>
    );
  }

  // Rendu par l'appelant, pas par le bouton : le bouton vit dans une ligne de <li>, et une modale née
  // là y resterait accrochée. Portalisée de toute façon, mais autant que l'arbre dise la vérité.
  const fixModal: ReactNode = jvid != null && fakeOdfOpen
    ? (
      <FakeOdfModal
        jvid={jvid}
        addressLabel={addressLabel ?? null}
        onClose={() => setFakeOdfOpen(false)}
        onAttached={() => { setFakeOdfOpen(false); onDone(); }}
      />
    )
    : null;

  return { renderFix, fixModal };
}
